import { Log } from "./logger";
import { GdprLevel, TelemetryEventBase } from "./internaltypes";
import { performHttpRequest, Response, RequestHttpOptions } from "./utils";

const LOGTAG = "telemetry";

// This file provides all the telemetry interfaces exchanged between clients and NvidiaStreamKit/SessionControl/other libraries.
// This file doesn't contain definition of any telemetry data collected by the libraries.
// Some of the common telemetry data definition is in internaltypes.ts. They are meant to be used by gamestream libraries and should not be exposed to clients.

/**
 * Details of the client which generated the telemetry data. @see TelemetryEventPayload
 * Every module has its own telemetry schema, module name and Id. The uploader of telemetry should not modify this data.
 * Note: The telemetry end points doesn't accept grouping of telemetry events from different client module.
 * Its the responsibility of uploader to group events from different client module.
 */
export declare interface ClientTelemetryConfig {
    /** The telemetry client's name as defined in the event schema. */
    clientName: string;
    /** The telemetry client's Id as defined in the event schema. */
    clientId: string;
    /** The telemetry client's event schema version. */
    eventSchemaVer: string;
}

/**
 * Event payload interface for all Telemetry events forwarded to clients.
 * @see TelemetryCallback
 */
export declare interface TelemetryEventPayload {
    /** Event name as per the schema. */
    name: string;
    /** Event payload as per the schema. */
    parameters: any;
    /** Event gdpr level as per the schema. */
    gdprLevel: string;
    /** Timestamp when the event was generated. */
    ts: string;
    /** Schema config to be used for dispatching the event. */
    clientConfig: ClientTelemetryConfig;
}

/**
 * Visualization and backend processing of certain telemetry events requires additional information like system properties, user streaming properties selection etc.
 * Instead of collecting all the info in every telemetry event, collecting just the Ids of events like SystemInfo in telemetry event will help in backend processing merging the required data.
 * @see Telemetry.updateTelemetryEventIds
 */
export declare interface TelemetryEventIds {
    /** An UUID sent with every StreamingProfile event by client. */
    streamingProfileGuid?: string;
    /** An UUID sent with every SystemInfo event by client. */
    systemInfoGuid?: string;
    /** Identifier of the last network test instance. */
    networkTestId?: string;
}

/**
 * Syntax of the individual events contained in the telemetry data object uploaded to server.
 * @see TelemetryCommonData
 */
export declare interface FormattedEventDetail {
    name: string;
    ts: string;
    parameters: object;
}

/**
 * Represents all the data to be sent to the telemetry server in every http request.
 * This structure is part of the schema definition, not all parameters populated will be sent to server.
 * The telemetry upload module will override many values.
 * @todo add more detailed documentation of each parameters and what fields will be overridden by the session control library.
 */
export declare interface TelemetryCommonData {
    clientId?: string;
    clientVer?: string;
    eventSchemaVer?: string;
    eventSysVer?: string;
    eventProtocol?: string;
    deviceId?: string;
    userId?: string;
    externalUserId?: string;
    idpId?: string;
    sessionId?: string;
    platform?: string;
    sentTs?: string;
    events?: FormattedEventDetail[];
    gdprFuncOptIn?: string; // this comes from starfleet via client
    gdprTechOptIn?: string;
    gdprBehOptIn?: string;
    deviceGdprFuncOptIn?: string; // this comes from EULA before login. Full when agree.
    deviceGdprTechOptIn?: string;
    deviceGdprBehOptIn?: string;
    deviceOS?: string;
    deviceOSVersion?: string;
    deviceType?: string;
    deviceModel?: string;
    deviceMake?: string;
    clientType?: string;
    clientVariant?: string;
    integrationId?: string;
    browserType?: string;
}

/**
 * Callback which handles the telemetry data provided by library.
 * @see TelemetryConfiguration
 */
export type TelemetryCallback = (telemetry: TelemetryEventPayload) => void;

/**
 * Informs the telemetry handling mechanism to NVIDIAStreamKit/SessionControl/other streaming related libraries.
 * Clients must decide the telemetry upload mechanism and set either url or callback option.
 */
export declare interface TelemetryConfiguration {
    /**
     * If set to string, will be treated as url where telemetry should be uploaded..
     * Else if callback is set then library will provide all telemetry in callback. It will be clients responsibility to upload telemetry to appropriate end point.
     */
    option: string | TelemetryCallback;
}

/**
 * Provides functionality for configuring the telemetry handling mechanism and caching data updates from clients.
 * Clients can either let the library upload telemetry data to the configured server endpoint or get telemetry data in callback.
 * @see TelemetryConfiguration for more details.
 */
export declare interface Telemetry {
    /**
     * Configure whether library should upload the telemetry or provide the telemetry data in onTelemetryEvent callback.
     * @param config - @see TelemetryConfiguration for details.
     * @returns - true if configured successfully. False if the configuration value is invalid.
     */
    configure(config: TelemetryConfiguration): boolean;

    /**
     * Provide the client/device/user specific information that needs to be sent to the server in http request.
     * Clients must call this every time user related parameters are changed.
     * This function must be invoked if clients want the library to upload the data.
     * If telemetry callback is set, this function can be optional and clients can fill the common data to the telemetry payload before uploading to the server.
     * @param data - common details part of the http request sent to server.
     */
    updateTelemetryCommonData(data: TelemetryCommonData): void;

    /**
     * Clients are responsible for collecting telemetry events like SystemInfo, user selected StreamingProfile, NetworkTest etc.
     * The above data is required in backend data processing of the events collected by NvidiaStreamKit/SessionControl/other libraries.
     * The Ids passed in this API will be collected in some of the telemetry events. The backend data processing merges appropriate data based on the Id.
     * @param ids - system info Id, streaming profile Id, network test Id etc.
     */
    updateTelemetryEventIds(ids: TelemetryEventIds): void;
}

/**
 * Wraps the implementation of caching/uploading/callback of telemetry.
 * All the libraries which provides telemetry support should override this class and invoke processEvent of this class.
 * This class should not be exported to clients.
 * todo: we don't rely on navigator.onLine or listeners for (offline/online). Need to evaluate its reliability on non browser platforms as this code
 * will be used in CEF framework on multiple platforms.
 */
export class TelemetryImpl implements Telemetry {
    protected telemetryCommonData?: TelemetryCommonData;
    protected telemetryEventIds: TelemetryEventIds = {};
    private clientTelemetryConfig: ClientTelemetryConfig;
    private serverUrl?: string = "";
    private telemetryCallback?: TelemetryCallback;
    private pendingEventPayloads: TelemetryEventPayload[] = [];
    private uploadToServerTimerId: number = 0;
    // todo: evaluate if we should use keep alive and other options.
    // In browser mode we might need platform details as well.
    private readonly httpConfiguration: RequestHttpOptions = {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Event-Protocol": "1.4" // todo: should this be generated from schema ?
        },
        body: "",
        retryCount: 3,
        timeout: 2000 // todo: is 2 seconds enough for this request?, what configurations does GFNClient, GFNClientSDK use?
    };

    public constructor(clientConfig: ClientTelemetryConfig) {
        this.clientTelemetryConfig = clientConfig;
    }

    public configure(config: TelemetryConfiguration): boolean {
        if (config.option) {
            // Check if the option property is a string or a callback function
            if (typeof config.option === "function") {
                Log.i("{95feb49}", "{01ded8b}");
                this.telemetryCallback = config.option;
                this.serverUrl = undefined;
                if (this.uploadToServerTimerId) {
                    window.clearTimeout(this.uploadToServerTimerId);
                    this.uploadToServerTimerId = 0;
                }
                for (const event of this.pendingEventPayloads) {
                    this.telemetryCallback(event);
                }
                this.pendingEventPayloads = [];
            } else if (typeof config.option === "string") {
                Log.i("{95feb49}", "{c76ea37}");
                this.serverUrl = config.option;
                this.telemetryCallback = undefined;
                if (this.isUserConsentTypeAvailable()) {
                    this.uploadTelemetry();
                }
            } else {
                // Invalid Option type
                Log.e("{95feb49}", "{328a30f}");
                return false;
            }
        } else {
            // No option property
            Log.e("{95feb49}", "{e936979}");
            return false;
        }
        return true;
    }

    public updateTelemetryCommonData(data: TelemetryCommonData): void {
        this.telemetryCommonData = {
            ...data,
            events: []
        };

        // If we are in upload configuration then initiate upload process if not already in progress.
        if (this.serverUrl) {
            // in case some telemetry was cached before user consent was available, ignore events which doesn't have consent.
            this.pendingEventPayloads = this.pendingEventPayloads.filter(event =>
                this.hasUserConsentedForEvent(event)
            );
            this.uploadTelemetry();
        }
    }

    public updateTelemetryEventIds(ids: TelemetryEventIds): void {
        this.telemetryEventIds = {
            ...ids
        };
    }

    /**
     * Processes the generated telemetry from the derived class.
     * If telemetry handling mechanism is not set through configure API then just cache in runtime memory.
     * Else:
     *      If callback is configured then pass the data to clients immediately.
     *      Else: telemetry upload to server:
     *          If consent not configured then cache in runtime memory.
     *          If user not consented for the current telemetry then discard it.
     *          Else upload the data to server.
     * @param event - Telemetry event types generated from schema.
     * @param priority - In telemetry upload configuration, if set, will use browsers sendBeacon API to upload the data to server.
     */
    protected processEvent(event: TelemetryEventBase<any>, priority?: boolean): void {
        const eventPayload: TelemetryEventPayload = {
            name: event.name,
            parameters: event.parameters,
            gdprLevel: event.gdprLevel,
            ts: event.ts,
            clientConfig: this.clientTelemetryConfig
        };

        if (this.telemetryCallback) {
            this.telemetryCallback(eventPayload);
        } else if (this.serverUrl && this.isUserConsentTypeAvailable()) {
            if (this.hasUserConsentedForEvent(eventPayload)) {
                if (priority) {
                    // This is a priority telemetry event, send it through sendBeacon API of browser.
                    const blobData = this.createTelemetryRequestData([eventPayload]);
                    const formData = new Blob([blobData], {
                        type: "text/plain"
                    });
                    let result = false;
                    result = navigator.sendBeacon(this.serverUrl, formData);
                    Log.i("{95feb49}", "{6267b70}", result, blobData);
                } else {
                    this.pendingEventPayloads.push(eventPayload);
                    this.uploadTelemetry();
                }
            } // else we ignore this event.
        } else {
            this.pendingEventPayloads.push(eventPayload);
        }
    }

    /**
     * Indicates if user consent type has been registered by clients.
     * @returns true - consent type available, else false.
     * @see hasUserConsentedForEvent to determine if a particular event can be sent to server.
     */
    private isUserConsentTypeAvailable(): boolean {
        return !!this.telemetryCommonData;
    }

    /**
     * Returns true if the event GDPR level has been consented by user to send the data to server.
     * @note  User consent might not be registered yet as EULA might not have been accepted by user.
     *        @see isUserConsentTypeAvailable to check if we have consent type registered by client.
     * @param event - Telemetry event whose GDPR level has to be checked with users GDPR selection.
     * @returns true - if the event can be sent to server else false.
     */
    private hasUserConsentedForEvent(event: TelemetryEventPayload): boolean {
        if (this.telemetryCommonData) {
            switch (event.gdprLevel) {
                case GdprLevel.Functional:
                    return (
                        this.telemetryCommonData.deviceGdprFuncOptIn === "Full" ||
                        this.telemetryCommonData.deviceGdprFuncOptIn === "Temp"
                    );
                case GdprLevel.Technical:
                    return this.telemetryCommonData.gdprTechOptIn === "Full";
                case GdprLevel.Behavioral:
                    return this.telemetryCommonData.gdprBehOptIn === "Full";
                default:
                    return false;
            }
        }
        return false;
    }

    /**
     * Generates the http request data to be sent to the telemetry server for all the cached telemetry events.
     * @returns http request data
     */
    private createTelemetryRequestData(events: TelemetryEventPayload[]): string {
        // Make a deep copy of common data before modifying any properties
        const commonData: TelemetryCommonData = {
            ...this.telemetryCommonData,
            events: []
        };

        commonData.clientId = this.clientTelemetryConfig.clientId;
        commonData.eventSchemaVer = this.clientTelemetryConfig.eventSchemaVer;
        commonData.sentTs = new Date().toISOString();
        for (const event of events) {
            const formattedEventDetail: FormattedEventDetail = {
                name: event.name,
                ts: event.ts!,
                parameters: event.parameters!
            };
            commonData.events?.push(formattedEventDetail);
        }
        return JSON.stringify(commonData);
    }

    /**
     * Initiates telemetry upload request if upload is not in progress or queued for uploading.
     * @param delay - optional delay(milliseconds) in uploading to server.
     */
    private uploadTelemetry(delay?: number) {
        if (this.pendingEventPayloads.length && !this.uploadToServerTimerId) {
            this.uploadToServerTimerId = window.setTimeout(
                () => this.performTelemetryUpload(),
                delay
            );
        }
    }

    /**
     * Uploads all the cached telemetry events to server in a single http request.
     * Once a request is attempted, the events are flushed irrespective of result of upload.
     * Once telemetry upload is completed and more events are queued we initiate next telemetry upload after some delay.
     */
    private performTelemetryUpload() {
        // note: even though not a likely case, if telemetry config is changed and we are in callback mode then return.
        if (!this.pendingEventPayloads.length || !this.serverUrl) {
            this.uploadToServerTimerId = 0;
            return;
        }

        const data = this.createTelemetryRequestData(this.pendingEventPayloads);
        // note: here we just attempt one http request and if it fails we would have flushed the array.
        // we could copy the array to another local variable and insert it into this.pendingEventPayloads in network failure case if we want to retry uploading it after sometime.
        this.pendingEventPayloads = [];
        performHttpRequest(this.serverUrl, { ...this.httpConfiguration, body: data })
            .then((response: Response) => {
                if (!(response.status >= 200 && response.status <= 299)) {
                    Log.e("{95feb49}", "{1dc64ee}", response.status);
                }
            })
            .catch((err: any) => {
                Log.e("{95feb49}", "{a0b4bea}", err);
            })
            .finally(() => {
                this.uploadToServerTimerId = 0;
                // If more events got added while performing an http request, delay for sometime to avoid overloading of server.
                if (this.pendingEventPayloads.length) {
                    this.uploadTelemetry(2000);
                }
            });
    }
}
